import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, Clock, Play, Square } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const lbl = "text-xs uppercase tracking-wider text-[#5C5651]";
const fieldCls = "mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]";
const hhmmss = (s) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((v) => String(v).padStart(2, "0")).join(":");

export default function TimeTrackingTab({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), label: "", hours: "", rate: "", billable: true, details: "" });

  // Live timer
  const [timerLabel, setTimerLabel] = useState("");
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  const tickRef = useRef(null);

  const load = () => api.get(`/projects/${projectId}/time-entries`).then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);
  useEffect(() => () => clearInterval(tickRef.current), []);

  const reset = () => setF({ date: new Date().toISOString().slice(0, 10), label: "", hours: "", rate: "", billable: true, details: "" });

  const startTimer = () => {
    startRef.current = Date.now();
    setElapsed(0);
    setRunning(true);
    tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
  };
  const stopTimer = () => {
    clearInterval(tickRef.current);
    setRunning(false);
    const secs = Math.floor((Date.now() - startRef.current) / 1000);
    const hrs = Math.max(0.01, +(secs / 3600).toFixed(2));
    const lastRate = entries[0]?.rate || "";
    setF({ date: new Date().toISOString().slice(0, 10), label: timerLabel, hours: String(hrs), rate: String(lastRate || ""), billable: true, details: "" });
    setTimerLabel("");
    setElapsed(0);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!Number(f.hours)) { toast.error("Enter the number of hours"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/time-entries`, {
        date: f.date, label: f.label, hours: Number(f.hours) || 0, rate: Number(f.rate) || 0,
        billable: f.billable, details: f.details,
      });
      setEntries((p) => [data, ...p]);
      reset(); setOpen(false);
      toast.success("Time entry added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/time-entries/${id}`); setEntries((p) => p.filter((x) => x.id !== id)); };

  const totals = useMemo(() => {
    const billableHrs = entries.filter((e) => e.billable).reduce((s, e) => s + (e.hours || 0), 0);
    const totalHrs = entries.reduce((s, e) => s + (e.hours || 0), 0);
    const amount = entries.filter((e) => e.billable).reduce((s, e) => s + (e.hours || 0) * (e.rate || 0), 0);
    return { billableHrs, totalHrs, amount };
  }, [entries]);

  return (
    <div className="max-w-5xl space-y-6" data-testid="time-tracking-tab">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Billing</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Time Tracking</h3>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-time-entry-btn"
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
              <Plus size={15} className="mr-2" /> Log Time
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#2B2622]">Log billable time</DialogTitle>
              <DialogDescription className="sr-only">Add a time tracking entry</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className={lbl}>Task</Label>
                <Input data-testid="time-label-input" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })}
                  placeholder="e.g. Site visit, Sourcing, Client call" className={fieldCls} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className={lbl}>Date</Label>
                  <Input data-testid="time-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={fieldCls} /></div>
                <div><Label className={lbl}>Hours</Label>
                  <Input data-testid="time-hours-input" type="number" min="0" step="0.01" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className={fieldCls} /></div>
                <div><Label className={lbl}>Rate ($/hr)</Label>
                  <Input data-testid="time-rate-input" type="number" min="0" step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} className={fieldCls} /></div>
              </div>
              <div>
                <Label className={lbl}>Details</Label>
                <Textarea data-testid="time-details-input" value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })}
                  placeholder="What did you work on?" className={fieldCls} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox data-testid="time-billable-checkbox" checked={f.billable} onCheckedChange={(v) => setF({ ...f, billable: !!v })}
                  className="rounded-none border-[#A68A6B] data-[state=checked]:bg-[#A68A6B] data-[state=checked]:border-[#A68A6B]" />
                <span className="text-sm text-[#2B2622]">Billable</span>
              </label>
              <Button data-testid="time-submit" type="submit" disabled={busy}
                className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                {busy ? "Saving…" : "Add Entry"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Live timer */}
      <div className="border border-[#EBE5E0] bg-white p-4 flex flex-wrap items-center gap-3" data-testid="time-timer">
        <div className="flex items-center gap-2 text-[#A68A6B]">
          <Clock size={18} strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-wider">Live timer</span>
        </div>
        <Input data-testid="timer-label-input" value={timerLabel} onChange={(e) => setTimerLabel(e.target.value)}
          disabled={running} placeholder="What are you working on?"
          className="flex-1 min-w-[200px] rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
        <span data-testid="timer-display" className={`font-mono text-2xl tabular-nums ${running ? "text-[#2B2622]" : "text-[#C9BBAE]"}`}>{hhmmss(elapsed)}</span>
        {!running ? (
          <Button data-testid="timer-start-btn" onClick={startTimer}
            className="rounded-none bg-[#2B2622] hover:bg-[#A68A6B] text-white uppercase tracking-wider text-xs py-5 px-5">
            <Play size={15} className="mr-2" /> Start
          </Button>
        ) : (
          <Button data-testid="timer-stop-btn" onClick={stopTimer}
            className="rounded-none bg-[#9E4747] hover:bg-[#843b3b] text-white uppercase tracking-wider text-xs py-5 px-5">
            <Square size={13} className="mr-2 fill-current" /> Stop & log
          </Button>
        )}
      </div>

      {entries.length > 0 && (
        <div className="grid grid-cols-3 gap-px bg-[#EBE5E0] border border-[#EBE5E0]" data-testid="time-summary">
          {[["Total Hours", totals.totalHrs.toFixed(2)], ["Billable Hours", totals.billableHrs.toFixed(2)], ["Billable Amount", fmtMoney(totals.amount)]].map(([k, v]) => (
            <div key={k} className="bg-white p-5">
              <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{k}</p>
              <p className="font-serif text-2xl text-[#2B2622] mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          <Clock size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#C9BBAE]" />
          No time logged yet. Click "Log Time" to track your billable hours.
        </div>
      ) : (
        <div className="border border-[#EBE5E0] overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#A68A6B] border-b border-[#EBE5E0]">
                <th className="p-4 font-medium">Date</th>
                <th className="p-4 font-medium">Task</th>
                <th className="p-4 font-medium text-right">Hours</th>
                <th className="p-4 font-medium text-right">Rate</th>
                <th className="p-4 font-medium text-right">Amount</th>
                <th className="p-4 font-medium">Billable</th>
                <th className="p-4 font-medium">Details</th>
                <th className="p-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-[#F2EFEB] last:border-0 align-top" data-testid={`time-entry-${e.id}`}>
                  <td className="p-4 text-[#2B2622] whitespace-nowrap">{e.date || "—"}</td>
                  <td className="p-4 text-[#2B2622]">{e.label || "—"}</td>
                  <td className="p-4 text-right text-[#2B2622]">{(e.hours || 0).toFixed(2)}</td>
                  <td className="p-4 text-right text-[#5C5651]">{e.rate ? fmtMoney(e.rate) : "—"}</td>
                  <td className="p-4 text-right text-[#2B2622]">{e.billable ? fmtMoney((e.hours || 0) * (e.rate || 0)) : "—"}</td>
                  <td className="p-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${e.billable ? "bg-[#EDE7DF] text-[#8C7154]" : "bg-[#F2EFEB] text-[#9b938b]"}`}>
                      {e.billable ? "Billable" : "Non-billable"}
                    </span>
                  </td>
                  <td className="p-4 text-[#5C5651] max-w-[260px] whitespace-pre-line">{e.details || "—"}</td>
                  <td className="p-4 text-right">
                    <button data-testid={`delete-time-${e.id}`} onClick={() => remove(e.id)} className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
